import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Typography,
  Chip,
  Stack,
  Paper,
} from "@mhome/ui";
import CloseIcon from "@mui/icons-material/Close";
import { useMediaQuery, useTheme } from "@mui/material";
import { useWebSocket } from "../ws/websocket";
import { useToast } from "../common/toast";
import { listCanvas, createEntityTwinCard } from "../service/ui-service";
import { toEntityTitleSubtitle } from "../common/string-helpers";
import { useRecoilValue } from "recoil";
import { safeAreaInsetsState } from "../global-state";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";

const CanvasSelectionDialog = ({ open, onClose, onConfirm, canvasList }) => {
  const theme = useTheme();

  const handleCanvasSelect = async (canvasId) => {
    await onConfirm(canvasId);
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      variant="pops"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Select Canvas</DialogTitle>
      <DialogContent>
        <div className="flex flex-col gap-2 pt-2">
          {canvasList.map((canvas) => (
            <Button
              key={canvas.id}
              variant="outlined"
              onClick={() => handleCanvasSelect(canvas.id)}
              className="justify-start normal-case"
            >
              {canvas.name}
            </Button>
          ))}
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCancel}
          variant="outlined"
          className="normal-case"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EntityDetailsDialog = ({ open, meta, entity, onClose }) => {
  const { successToast, errorToast } = useToast();
  const { sendAndWaitWs } = useWebSocket();
  const theme = useTheme();
  const getComponentBgColor = useComponentBackgroundColor();
  const [canvasDialogOpen, setCanvasDialogOpen] = useState(false);
  const [canvasList, setCanvasList] = useState([]);
  const safeAreaInsets = useRecoilValue(safeAreaInsetsState);
  const isSmallScreen = useMediaQuery("@media (max-width:959px)");

  const handleListCanvas = async () => {
    const resp = await listCanvas(sendAndWaitWs);
    if (resp.canvas) {
      setCanvasList(resp.canvas);
    } else {
      errorToast("Get Canvas Error");
    }
  };

  const formatCapability = (capability) => {
    return meta.capabilityNames[capability] || capability;
  };

  const getIntegrationName = (integration) => {
    return meta.integrationNames[integration] || integration;
  };

  const getTwinName = (twinTag) => {
    return meta.twinNames[twinTag] || twinTag;
  };

  const groupedCapabilities = entity?.capabilities?.reduce(
    (acc, capability) => {
      let type = capability.split(".")[0];
      if (type !== "command") {
        type = "data";
      }
      if (!acc[type]) acc[type] = [];
      acc[type].push(capability);
      return acc;
    },
    {}
  );

  const handleAddTwinClick = async () => {
    await handleListCanvas();
    setCanvasDialogOpen(true);
  };

  const handleCanvasSelect = async (canvasId) => {
    await handleAddTwin(canvasId);
    setCanvasDialogOpen(false);
  };

  const handleAddTwin = async (id) => {
    const resp = await createEntityTwinCard(
      sendAndWaitWs,
      id,
      entity.integration,
      entity.id,
      entity.name,
      entity.type,
      entity.twinTags || [],
      false,
      entity.twinSig
    );
    if (resp) {
      successToast("Success");
    } else {
      errorToast("Error");
    }
  };

  const handleClose = () => {
    onClose();
  };

  const entityInfo = entity?.name
    ? toEntityTitleSubtitle(entity.name, true)
    : null;
  const displayTitle =
    entityInfo && typeof entityInfo === "object"
      ? entityInfo.title
      : entity?.name || "";
  const displaySubtitle =
    entityInfo && typeof entityInfo === "object"
      ? entityInfo.subtitle
      : entity?.integration
      ? getIntegrationName(entity.integration)
      : "";

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen={isSmallScreen}
        variant={isSmallScreen ? undefined : "drawer"}
        PaperProps={{
          style: {
            width: isSmallScreen ? "100%" : "600px",
            marginTop: isSmallScreen ? 0 : `${safeAreaInsets.top}px !important`,
            marginBottom: isSmallScreen
              ? 0
              : `${safeAreaInsets.bottom}px !important`,
            height: isSmallScreen
              ? "100vh"
              : `calc(100vh - ${
                  safeAreaInsets.top + safeAreaInsets.bottom
                }px) !important`,
            maxHeight: isSmallScreen
              ? "100vh"
              : `calc(100vh - ${
                  safeAreaInsets.top + safeAreaInsets.bottom
                }px) !important`,
            paddingTop: isSmallScreen ? `${safeAreaInsets.top}px` : 0,
            paddingBottom: isSmallScreen ? `${safeAreaInsets.bottom}px` : 0,
            paddingRight: `${safeAreaInsets.right}px`,
          },
        }}
      >
        <DialogTitle className="gap-2 flex items-center min-h-0">
          <IconButton
            color="inherit"
            onClick={handleClose}
            aria-label="close"
            className="flex-shrink-0"
          >
            <CloseIcon />
          </IconButton>
          <Typography
            variant="h6"
            component="div"
            className="leading-tight mb-0"
          >
            Entity Details
          </Typography>
        </DialogTitle>

        <DialogContent className="px-4 pb-4 flex flex-col gap-6 overflow-hidden">
          <Paper
            variant="outlined"
            className="p-4"
            style={{
              backgroundColor: getComponentBgColor("hsl(var(--card))"),
            }}
          >
            <Typography variant="h6" gutterBottom>
              {displayTitle}
            </Typography>
            <Typography variant="subtitle2" color="textSecondary">
              {displaySubtitle}
            </Typography>
          </Paper>

          <Paper
            variant="outlined"
            className="p-4"
            style={{
              backgroundColor: getComponentBgColor("hsl(var(--card))"),
            }}
          >
            {/* Capabilities Section */}
            {/* Data Capabilities */}
            <div className="mb-4">
              <div
                className="border-b pb-2 mb-2"
                style={{
                  borderColor: "hsl(var(--border))",
                }}
              >
                <Typography variant="subtitle2">Available Data</Typography>
              </div>
              {groupedCapabilities?.data ? (
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  className="gap-1"
                >
                  {groupedCapabilities.data.map((capability) => (
                    <Chip
                      key={capability}
                      label={formatCapability(capability)}
                      size="small"
                      style={{
                        backgroundColor: "hsl(var(--primary) / 0.1)",
                        color: "hsl(var(--primary))",
                        height: 24,
                      }}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className="italic py-2"
                >
                  No available data
                </Typography>
              )}
            </div>

            {/* Command Capabilities */}
            <div className="mb-0">
              <div
                className="border-b pb-2 mb-2"
                style={{
                  borderColor: "hsl(var(--border))",
                }}
              >
                <Typography variant="subtitle2">Available Commands</Typography>
              </div>
              {groupedCapabilities?.command ? (
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  className="gap-1"
                >
                  {groupedCapabilities.command.map((capability) => (
                    <Chip
                      key={capability}
                      label={formatCapability(capability)}
                      size="small"
                      style={{
                        backgroundColor: "#4caf501A",
                        color: "#4caf50",
                        height: 24,
                      }}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className="italic py-2"
                >
                  No available commands
                </Typography>
              )}
            </div>
          </Paper>

          {/* Twin Tags Section */}
          {entity?.twinTags && entity.twinTags.length > 0 && (
            <Paper
              variant="outlined"
              style={{
                backgroundColor: getComponentBgColor("hsl(var(--card))"),
              }}
            >
              <div
                className="flex justify-between items-center mx-4 py-2 border-b"
                style={{
                  borderColor: "hsl(var(--border))",
                }}
              >
                <div className="flex items-center gap-2">
                  <Typography variant="subtitle2">
                    Add Dashboard Card
                  </Typography>
                  <div className="flex gap-2">
                    {entity?.newTwin === true ? (
                      <Chip
                        label="New version"
                        size="small"
                        color="info"
                        variant="outlined"
                        style={{
                          height: 20,
                          fontSize: "0.65rem",
                          fontWeight: 500,
                        }}
                      />
                    ) : (
                      <></>
                    )}
                    {entity?.cardCount && entity.cardCount > 0 ? (
                      <Chip
                        label="Already added"
                        size="small"
                        color="default"
                        variant="outlined"
                        style={{
                          height: 20,
                          fontSize: "0.65rem",
                          fontWeight: 500,
                        }}
                      />
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleAddTwinClick}
                  disabled={
                    !entity?.twinTags ||
                    entity.twinTags.length === 0 ||
                    (entity?.cardCount ?? 0) > 0
                  }
                  className="normal-case"
                  style={{ boxShadow: "none" }}
                >
                  Add
                </Button>
              </div>
              <div className="p-4">
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className="mb-2 font-medium"
                >
                  Available capabilities:
                </Typography>
                <Stack spacing={1}>
                  {entity.twinTags.map((tag) => (
                    <Typography key={tag} variant="body2" className="text-sm">
                      • {getTwinName(tag)}
                    </Typography>
                  ))}
                </Stack>
              </div>
            </Paper>
          )}
        </DialogContent>
      </Dialog>
      <CanvasSelectionDialog
        open={canvasDialogOpen}
        onClose={() => setCanvasDialogOpen(false)}
        onConfirm={handleCanvasSelect}
        canvasList={canvasList}
      />
    </>
  );
};

export default EntityDetailsDialog;
